import React, { useEffect, useRef, useState } from 'react'

import { Box, IconButton, Typography } from '@material-ui/core'
import { ArrowBack } from '@material-ui/icons'
import { isEmpty } from 'lodash'

import { LoadingFeedback } from 'components'

import { ExerciseQuiz, ExerciseReview } from './components'
import { LessonSidebar } from '../LessonContent/components'

import useAuthTraining from 'hooks/useAuthTraining'
import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'
import helpers from 'helpers'
import * as service from 'service'
import { routes } from 'TrainingRoutes'
import useStyles from './styles'

const fetchLesson = ({ courseId, lessonId }) =>
  service.trainingPortal.courses.lesson(courseId, lessonId)

const fetchExercise = ({ courseId, lessonId }) =>
  service.trainingPortal.courses.exercise(courseId, lessonId)

const fetchAttempts = ({ courseId, lessonId }) =>
  service.trainingPortal.courses.exerciseAttempts(courseId, lessonId)

const fetchModules = ({ courseId }) =>
  service.trainingPortal.courses.modules(courseId)

const fetchCourse = ({ courseId }) =>
  service.trainingPortal.courses.show(courseId)

const ExerciseContent = ({ match, history }) => {
  const classes = useStyles()
  const snackbar = useSnackbar()
  const { portalToken: contextPortalToken } = useAuthTraining()

  const portalToken = match?.params?.portalToken || contextPortalToken
  const courseId = match?.params?.courseId
  const lessonId = match?.params?.lessonId

  const [phase, setPhase] = useState('quiz')
  const [attempts, setAttempts] = useState([])
  const [focusAttemptId, setFocusAttemptId] = useState(null)
  const [attemptsCount, setAttemptsCount] = useState(0)
  const [canAttempt, setCanAttempt] = useState(true)
  const [remainingAttempts, setRemainingAttempts] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isSubmittingRef = useRef(false)

  const lessonParams = { courseId, lessonId }
  const courseParams = { courseId }

  const { response: lessonResponse, isLoading: isLoadingLesson } = useFetch(
    fetchLesson,
    lessonParams,
    [lessonId],
  )

  const { response: exerciseResponse, isLoading: isLoadingExercise } = useFetch(
    fetchExercise,
    lessonParams,
    [lessonId],
  )

  const { response: modulesResponse, isLoading: isLoadingModules } = useFetch(
    fetchModules,
    courseParams,
    [lessonId],
  )

  const { response: courseResponse, isLoading: isLoadingCourse } = useFetch(
    fetchCourse,
    courseParams,
    [lessonId],
  )

  const lesson = lessonResponse?.data?.lesson
  const exercise = exerciseResponse?.data?.exercise
  const modules = modulesResponse?.data?.modules || []
  const courseTitle = courseResponse?.data?.course?.title || ''

  const { response: attemptsResponse, isLoading: isLoadingAttempts } = useFetch(
    fetchAttempts,
    lessonParams,
    [lessonId],
    attemptsCount > 0,
  )

  useEffect(() => {
    setPhase('quiz')
    setAttempts([])
    setFocusAttemptId(null)
  }, [lessonId])

  useEffect(() => {
    const data = exerciseResponse?.data
    if (!data) return
    setAttemptsCount(data.attemptsCount ?? 0)
    setCanAttempt(data.canAttempt ?? true)
    setRemainingAttempts(data.remainingAttempts ?? null)
  }, [exerciseResponse])

  useEffect(() => {
    const data = attemptsResponse?.data
    if (!data) return
    setAttempts(data.attempts ?? [])
    setAttemptsCount(data.attemptsCount ?? 0)
    setCanAttempt(data.canAttempt ?? true)
    setRemainingAttempts(data.remainingAttempts ?? null)
    if (!isEmpty(data.attempts)) setPhase('review')
  }, [attemptsResponse])

  const isLoading =
    isLoadingLesson ||
    isLoadingExercise ||
    isLoadingModules ||
    isLoadingCourse ||
    isLoadingAttempts

  const handleExerciseSubmit = async (answers) => {
    if (isSubmittingRef.current) return
    isSubmittingRef.current = true
    setIsSubmitting(true)
    try {
      const response = await service.trainingPortal.courses.submitExercise(
        courseId,
        lessonId,
        answers,
      )
      const newAttempt = response?.data?.attempt
      setAttempts((previousAttempts) => [...previousAttempts, newAttempt])
      setAttemptsCount(response?.data?.attemptsCount ?? 0)
      setCanAttempt(response?.data?.canAttempt ?? true)
      setRemainingAttempts(response?.data?.remainingAttempts ?? null)
      setFocusAttemptId(newAttempt?.id)
      setPhase('review')
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao enviar respostas.',
        variant: 'error',
      })
    } finally {
      isSubmittingRef.current = false
      setIsSubmitting(false)
    }
  }

  const handleRetake = () => setPhase('quiz')

  const handleBack = () => {
    history.push(
      routes.courseDetail
        .replace(':portalToken', portalToken)
        .replace(':courseId', courseId),
    )
  }

  const handlePrevLesson = () => {
    if (!lesson?.prevLessonId) return
    const targetRoute =
      lesson.prevLessonKind === 'exercise'
        ? routes.exerciseContent
        : routes.lessonContent
    history.push(
      targetRoute
        .replace(':portalToken', portalToken)
        .replace(':courseId', courseId)
        .replace(':lessonId', lesson.prevLessonId),
    )
  }

  const handleNextLesson = () => {
    if (lesson?.nextLessonId) {
      const targetRoute =
        lesson.nextLessonKind === 'exercise'
          ? routes.exerciseContent
          : routes.lessonContent
      history.push(
        targetRoute
          .replace(':portalToken', portalToken)
          .replace(':courseId', courseId)
          .replace(':lessonId', lesson.nextLessonId),
      )
    } else {
      history.push(
        routes.courseCertificate
          .replace(':portalToken', portalToken)
          .replace(':courseId', courseId),
      )
    }
  }

  return (
    <Box className={classes.root}>
      <LoadingFeedback open={isLoading} />

      {!isLoading && (
        <>
          <Box className={classes.breadcrumb}>
            <IconButton
              className={classes.backButton}
              size="small"
              onClick={handleBack}
            >
              <ArrowBack fontSize="small" />
            </IconButton>
            <Typography className={classes.courseTitle}>
              {courseTitle}
            </Typography>
          </Box>

          <Box className={classes.body}>
            <Box className={classes.mainContent}>
              {phase === 'quiz' && (
                <ExerciseQuiz
                  exercise={exercise}
                  onSubmit={handleExerciseSubmit}
                  isSubmitting={isSubmitting}
                />
              )}
              {phase === 'review' && (
                <ExerciseReview
                  attempts={attempts}
                  focusAttemptId={focusAttemptId}
                  canAttempt={canAttempt}
                  remainingAttempts={remainingAttempts}
                  onRetake={handleRetake}
                  prevLessonId={lesson?.prevLessonId}
                  onPrevLesson={handlePrevLesson}
                  nextLessonId={lesson?.nextLessonId}
                  onNextLesson={handleNextLesson}
                  isLastLesson={!lesson?.nextLessonId}
                />
              )}
            </Box>
            <LessonSidebar
              modules={modules}
              activeLessonId={lessonId}
              portalToken={portalToken}
              courseId={courseId}
              history={history}
            />
          </Box>
        </>
      )}
    </Box>
  )
}

export default ExerciseContent
